import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Building2, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle,
  Loader2,
  Upload,
  FileText,
  Users,
  DollarSign,
  Shield,
  Scale,
  Briefcase,
  X,
  File,
  AlertCircle,
  ClipboardCheck
} from "lucide-react";
import { useAuth } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_CATEGORIES = [
  { id: "application_fee_proof", name: "Proof of Application Fee", required: true },
  { id: "registration_license", name: "Registration Certificate & RBZ License", required: true },
  { id: "cr5_cr6_cr16", name: "CR5, CR6, and CR16 Copies", required: true },
  { id: "directors_ids_cvs", name: "Directors IDs & Signed CVs", required: true },
  { id: "org_chart", name: "Organizational Chart", required: true },
  { id: "business_plan", name: "Business Plan (3 years)", required: true },
  { id: "loan_portfolio_report", name: "Loan Portfolio Report (6 months)", required: true },
  { id: "audited_financials", name: "Audited Financial Statements (3 years)", required: true },
  { id: "collateral_description", name: "Collateral Description", required: true },
  { id: "creditor_references", name: "Creditor References (min 3)", required: true },
  { id: "board_resolution", name: "Board Resolution to Borrow", required: true },
  { id: "tax_clearance", name: "Current Tax Clearance", required: true },
  { id: "bank_statements", name: "Recent Bank Statements", required: true },
  { id: "other_documents", name: "Other Relevant Documents", required: false }
];

const WholesaleApplication = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [documents, setDocuments] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    // Branch & Officer Info
    branch: "",
    officer: "",
    application_date: new Date().toISOString().split('T')[0],
    
    // Business Data
    institution_name: "",
    company_reg_number: "",
    year_incorporated: "",
    registered_address: "",
    property_ownership: "",
    contact_number: "",
    email_address: "",
    primary_contact_name: "",
    primary_contact_position: "",
    primary_contact_phone: "",
    primary_contact_email: "",
    
    // Legal & Regulatory Status
    license_number: "",
    license_type: "",
    year_licensed: "",
    tax_registration: "",
    compliance_status: "",
    
    // Funding/Loan Details
    amount_required: "",
    tenure_months: "",
    custom_tenure: "",
    own_capital_available: "",
    purpose_of_loan: "",
    preferred_terms: "",
    expected_impact: "",
    
    // Financial Details - Existing Loans
    has_existing_loans: "no",
    existing_loans: [{
      lender: "",
      loan_type: "",
      loan_amount: "",
      current_balance: "",
      tenure: "",
      monthly_repayment: ""
    }],
    
    // Financial & Business Model
    total_assets: "",
    total_liabilities: "",
    capital: "",
    interest_income: "",
    gross_income: "",
    net_profit_loss: "",
    products: "",
    customer_segments: "",
    geographical_coverage: [],
    number_of_clients: "",
    gender_distribution_male: "",
    gender_distribution_female: "",
    
    // Security/Collateral
    collateral_items: [{
      asset_type: "",
      registered_owner: "",
      description: "",
      market_value: ""
    }],
    
    // Declaration & Consent
    declaration_agreed: false,
    consent_identity_verification: false,
    consent_credit_bureau: false,
    application_fee_amount: ""
  });

  const totalSteps = 9; // Added review step
  const progress = (step / totalSteps) * 100;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleExistingLoanChange = (index, field, value) => {
    setFormData(prev => {
      const loans = [...prev.existing_loans];
      loans[index] = { ...loans[index], [field]: value };
      return { ...prev, existing_loans: loans };
    });
  };

  const addExistingLoan = () => {
    setFormData(prev => ({
      ...prev,
      existing_loans: [...prev.existing_loans, {
        lender: "", loan_type: "", loan_amount: "", current_balance: "", tenure: "", monthly_repayment: ""
      }]
    }));
  };

  const handleCollateralChange = (index, field, value) => {
    setFormData(prev => {
      const items = [...prev.collateral_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, collateral_items: items };
    });
  };

  const addCollateralItem = () => {
    setFormData(prev => ({
      ...prev,
      collateral_items: [...prev.collateral_items, {
        asset_type: "", registered_owner: "", description: "", market_value: ""
      }]
    }));
  };

  const handleGeographicalChange = (value) => {
    setFormData(prev => {
      const coverage = prev.geographical_coverage.includes(value)
        ? prev.geographical_coverage.filter(v => v !== value)
        : [...prev.geographical_coverage, value];
      return { ...prev, geographical_coverage: coverage };
    });
  };

  const handleFileUpload = async (categoryId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    setUploadingDoc(categoryId);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocuments(prev => ({
          ...prev,
          [categoryId]: {
            name: file.name,
            size: file.size,
            type: file.type,
            data: reader.result,
            uploadedAt: new Date().toISOString()
          }
        }));
        toast.success(`${file.name} uploaded`);
        setUploadingDoc(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Upload failed");
      setUploadingDoc(null);
    }
  };

  const removeDocument = (categoryId) => {
    setDocuments(prev => {
      const updated = { ...prev };
      delete updated[categoryId];
      return updated;
    });
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.institution_name && formData.company_reg_number && formData.registered_address;
      case 2:
        return formData.license_number && formData.license_type && formData.compliance_status;
      case 3:
        return formData.amount_required && (formData.tenure_months || formData.custom_tenure) && formData.purpose_of_loan;
      case 4:
        return true; // Existing loans section is optional
      case 5:
        return formData.total_assets && formData.total_liabilities && formData.capital;
      case 6:
        return formData.collateral_items[0].asset_type && formData.collateral_items[0].market_value;
      case 7:
        return Object.keys(documents).length >= 5; // At least 5 documents
      case 8:
        return true; // Review step - always valid
      case 9:
        return formData.declaration_agreed && formData.consent_identity_verification && formData.consent_credit_bureau;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!formData.declaration_agreed || !formData.consent_identity_verification || !formData.consent_credit_bureau) {
      toast.error("Please provide all required consents");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        applicant_type: "wholesale_mfi",
        loan_type: "wholesale_lending",
        amount: parseFloat(formData.amount_required) || 0,
        term_months: formData.tenure_months === "custom" 
          ? parseInt(formData.custom_tenure) || 12 
          : parseInt(formData.tenure_months) || 12,
        documents_meta: Object.keys(documents).reduce((acc, key) => {
          acc[key] = {
            name: documents[key].name,
            size: documents[key].size,
            type: documents[key].type,
            uploadedAt: documents[key].uploadedAt
          };
          return acc;
        }, {})
      };

      const response = await axios.post(
        `${API}/wholesale/apply`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const appId = response.data.application_id;
      setApplicationId(appId);

      // Upload documents
      if (Object.keys(documents).length > 0) {
        try {
          await axios.post(
            `${API}/documents/upload`,
            { application_id: appId, documents },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (docError) {
          console.error("Document upload error:", docError);
        }
      }

      toast.success("Application submitted successfully!");
      setStep(totalSteps + 1); // Success step
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return "Business Data";
      case 2: return "Legal & Regulatory Status";
      case 3: return "Funding Details";
      case 4: return "Existing Financial Obligations";
      case 5: return "Financial & Business Model";
      case 6: return "Security / Collateral";
      case 7: return "Supporting Documents";
      case 8: return "Review Application";
      case 9: return "Declaration & Consent";
      default: return "Application Complete";
    }
  };

  const getStepIcon = () => {
    switch (step) {
      case 1: return Building2;
      case 2: return Scale;
      case 3: return DollarSign;
      case 4: return Briefcase;
      case 5: return FileText;
      case 6: return Shield;
      case 7: return Upload;
      case 8: return ClipboardCheck;
      case 9: return CheckCircle;
      default: return CheckCircle;
    }
  };

  const StepIcon = getStepIcon();

  // Render steps
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Name of Financial Institution *</Label>
                <Input
                  value={formData.institution_name}
                  onChange={(e) => handleInputChange("institution_name", e.target.value)}
                  placeholder="Enter institution name"
                />
              </div>
              <div>
                <Label>Company Registration Number *</Label>
                <Input
                  value={formData.company_reg_number}
                  onChange={(e) => handleInputChange("company_reg_number", e.target.value)}
                  placeholder="e.g., 1234/2020"
                />
              </div>
              <div>
                <Label>Year Incorporated</Label>
                <Input
                  type="number"
                  value={formData.year_incorporated}
                  onChange={(e) => handleInputChange("year_incorporated", e.target.value)}
                  placeholder="e.g., 2015"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Registered / Office Address *</Label>
                <Textarea
                  value={formData.registered_address}
                  onChange={(e) => handleInputChange("registered_address", e.target.value)}
                  placeholder="Enter full address"
                  rows={2}
                />
              </div>
              <div>
                <Label>Property Ownership</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="property_ownership"
                      value="owned"
                      checked={formData.property_ownership === "owned"}
                      onChange={(e) => handleInputChange("property_ownership", e.target.value)}
                      className="w-4 h-4"
                    />
                    <span>Owned</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="property_ownership"
                      value="rented"
                      checked={formData.property_ownership === "rented"}
                      onChange={(e) => handleInputChange("property_ownership", e.target.value)}
                      className="w-4 h-4"
                    />
                    <span>Rented</span>
                  </label>
                </div>
              </div>
              <div>
                <Label>Contact Number</Label>
                <Input
                  value={formData.contact_number}
                  onChange={(e) => handleInputChange("contact_number", e.target.value)}
                  placeholder="+263..."
                />
              </div>
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => handleInputChange("email_address", e.target.value)}
                  placeholder="info@institution.com"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-700 mb-4">Primary Contact Person</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={formData.primary_contact_name}
                    onChange={(e) => handleInputChange("primary_contact_name", e.target.value)}
                    placeholder="Contact person name"
                  />
                </div>
                <div>
                  <Label>Position / Title</Label>
                  <Input
                    value={formData.primary_contact_position}
                    onChange={(e) => handleInputChange("primary_contact_position", e.target.value)}
                    placeholder="e.g., CEO, Managing Director"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.primary_contact_phone}
                    onChange={(e) => handleInputChange("primary_contact_phone", e.target.value)}
                    placeholder="+263..."
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.primary_contact_email}
                    onChange={(e) => handleInputChange("primary_contact_email", e.target.value)}
                    placeholder="contact@institution.com"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>License Number *</Label>
                <Input
                  value={formData.license_number}
                  onChange={(e) => handleInputChange("license_number", e.target.value)}
                  placeholder="RBZ License Number"
                />
              </div>
              <div>
                <Label>License Type *</Label>
                <Input
                  value={formData.license_type}
                  onChange={(e) => handleInputChange("license_type", e.target.value)}
                  placeholder="e.g., Microfinance, Money Lending"
                />
              </div>
              <div>
                <Label>Year Licensed</Label>
                <Input
                  type="number"
                  value={formData.year_licensed}
                  onChange={(e) => handleInputChange("year_licensed", e.target.value)}
                  placeholder="e.g., 2018"
                />
              </div>
              <div>
                <Label>Tax Registration Number</Label>
                <Input
                  value={formData.tax_registration}
                  onChange={(e) => handleInputChange("tax_registration", e.target.value)}
                  placeholder="ZIMRA Tax Number"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Compliance Status *</Label>
                <Textarea
                  value={formData.compliance_status}
                  onChange={(e) => handleInputChange("compliance_status", e.target.value)}
                  placeholder="Describe current compliance status with regulatory requirements"
                  rows={3}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Amount Required (USD) *</Label>
                <Input
                  type="number"
                  value={formData.amount_required}
                  onChange={(e) => handleInputChange("amount_required", e.target.value)}
                  placeholder="25,000 - 100,000"
                />
                <p className="text-xs text-gray-500 mt-1">Min: $25,000 | Max: $100,000</p>
              </div>
              <div>
                <Label>Tenure *</Label>
                <Select
                  value={formData.tenure_months}
                  onValueChange={(v) => {
                    handleInputChange("tenure_months", v);
                    if (v !== "custom") handleInputChange("custom_tenure", "");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenure" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                    <SelectItem value="18">18 months</SelectItem>
                    <SelectItem value="24">24 months</SelectItem>
                    <SelectItem value="36">36 months</SelectItem>
                    <SelectItem value="custom">Custom tenure</SelectItem>
                  </SelectContent>
                </Select>
                {formData.tenure_months === "custom" && (
                  <div className="mt-2">
                    <Input
                      type="number"
                      value={formData.custom_tenure}
                      onChange={(e) => handleInputChange("custom_tenure", e.target.value)}
                      placeholder="Enter custom tenure in months"
                      min="1"
                      max="60"
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Standard: 6 to 36 months</p>
              </div>
              <div>
                <Label>Own Capital Available (USD)</Label>
                <Input
                  type="number"
                  value={formData.own_capital_available}
                  onChange={(e) => handleInputChange("own_capital_available", e.target.value)}
                  placeholder="Enter amount"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Purpose of Loan *</Label>
                <Textarea
                  value={formData.purpose_of_loan}
                  onChange={(e) => handleInputChange("purpose_of_loan", e.target.value)}
                  placeholder="Describe how the funds will be used to support productive lending"
                  rows={3}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Preferred Terms</Label>
                <Textarea
                  value={formData.preferred_terms}
                  onChange={(e) => handleInputChange("preferred_terms", e.target.value)}
                  placeholder="Any specific terms or conditions you prefer"
                  rows={2}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Expected Impact</Label>
                <Textarea
                  value={formData.expected_impact}
                  onChange={(e) => handleInputChange("expected_impact", e.target.value)}
                  placeholder="Describe expected impact on your lending operations and clients served"
                  rows={3}
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <Label>Do you have existing loans or have borrowed elsewhere before?</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="has_existing_loans"
                    value="yes"
                    checked={formData.has_existing_loans === "yes"}
                    onChange={(e) => handleInputChange("has_existing_loans", e.target.value)}
                    className="w-4 h-4"
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="has_existing_loans"
                    value="no"
                    checked={formData.has_existing_loans === "no"}
                    onChange={(e) => handleInputChange("has_existing_loans", e.target.value)}
                    className="w-4 h-4"
                  />
                  <span>No</span>
                </label>
              </div>
            </div>

            {formData.has_existing_loans === "yes" && (
              <div className="space-y-4">
                {formData.existing_loans.map((loan, index) => (
                  <Card key={index} className="bg-gray-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Existing Loan {index + 1}</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Lender</Label>
                        <Input
                          value={loan.lender}
                          onChange={(e) => handleExistingLoanChange(index, "lender", e.target.value)}
                          placeholder="Lender name"
                        />
                      </div>
                      <div>
                        <Label>Type of Loan</Label>
                        <Input
                          value={loan.loan_type}
                          onChange={(e) => handleExistingLoanChange(index, "loan_type", e.target.value)}
                          placeholder="e.g., Term Loan"
                        />
                      </div>
                      <div>
                        <Label>Loan Amount (USD)</Label>
                        <Input
                          type="number"
                          value={loan.loan_amount}
                          onChange={(e) => handleExistingLoanChange(index, "loan_amount", e.target.value)}
                          placeholder="Original amount"
                        />
                      </div>
                      <div>
                        <Label>Current Balance (USD)</Label>
                        <Input
                          type="number"
                          value={loan.current_balance}
                          onChange={(e) => handleExistingLoanChange(index, "current_balance", e.target.value)}
                          placeholder="Outstanding balance"
                        />
                      </div>
                      <div>
                        <Label>Tenure (months)</Label>
                        <Input
                          type="number"
                          value={loan.tenure}
                          onChange={(e) => handleExistingLoanChange(index, "tenure", e.target.value)}
                          placeholder="Loan term"
                        />
                      </div>
                      <div>
                        <Label>Monthly Repayment (USD)</Label>
                        <Input
                          type="number"
                          value={loan.monthly_repayment}
                          onChange={(e) => handleExistingLoanChange(index, "monthly_repayment", e.target.value)}
                          placeholder="Monthly payment"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button type="button" variant="outline" onClick={addExistingLoan}>
                  + Add Another Loan
                </Button>
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Total Assets (USD) *</Label>
                <Input
                  type="number"
                  value={formData.total_assets}
                  onChange={(e) => handleInputChange("total_assets", e.target.value)}
                  placeholder="Enter total assets"
                />
              </div>
              <div>
                <Label>Total Liabilities (USD) *</Label>
                <Input
                  type="number"
                  value={formData.total_liabilities}
                  onChange={(e) => handleInputChange("total_liabilities", e.target.value)}
                  placeholder="Enter total liabilities"
                />
              </div>
              <div>
                <Label>Capital (USD) *</Label>
                <Input
                  type="number"
                  value={formData.capital}
                  onChange={(e) => handleInputChange("capital", e.target.value)}
                  placeholder="Enter capital"
                />
              </div>
              <div>
                <Label>Interest Income (USD)</Label>
                <Input
                  type="number"
                  value={formData.interest_income}
                  onChange={(e) => handleInputChange("interest_income", e.target.value)}
                  placeholder="Annual interest income"
                />
              </div>
              <div>
                <Label>Gross Income (USD)</Label>
                <Input
                  type="number"
                  value={formData.gross_income}
                  onChange={(e) => handleInputChange("gross_income", e.target.value)}
                  placeholder="Annual gross income"
                />
              </div>
              <div>
                <Label>Net Profit/Loss (USD)</Label>
                <Input
                  type="number"
                  value={formData.net_profit_loss}
                  onChange={(e) => handleInputChange("net_profit_loss", e.target.value)}
                  placeholder="Enter amount (negative for loss)"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-700 mb-4">Business Model Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Products Offered</Label>
                  <Textarea
                    value={formData.products}
                    onChange={(e) => handleInputChange("products", e.target.value)}
                    placeholder="List your loan products and services"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Customer Segments</Label>
                  <Textarea
                    value={formData.customer_segments}
                    onChange={(e) => handleInputChange("customer_segments", e.target.value)}
                    placeholder="e.g., SMEs, Traders, Farmers"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Geographical Coverage</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.geographical_coverage.includes("urban")}
                        onChange={() => handleGeographicalChange("urban")}
                        className="w-4 h-4"
                      />
                      <span>Urban</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.geographical_coverage.includes("rural")}
                        onChange={() => handleGeographicalChange("rural")}
                        className="w-4 h-4"
                      />
                      <span>Rural</span>
                    </label>
                  </div>
                </div>
                <div>
                  <Label>Number of Clients Served</Label>
                  <Input
                    type="number"
                    value={formData.number_of_clients}
                    onChange={(e) => handleInputChange("number_of_clients", e.target.value)}
                    placeholder="Total active clients"
                  />
                </div>
                <div>
                  <Label>Gender Distribution - Male (%)</Label>
                  <Input
                    type="number"
                    value={formData.gender_distribution_male}
                    onChange={(e) => handleInputChange("gender_distribution_male", e.target.value)}
                    placeholder="e.g., 45"
                  />
                </div>
                <div>
                  <Label>Gender Distribution - Female (%)</Label>
                  <Input
                    type="number"
                    value={formData.gender_distribution_female}
                    onChange={(e) => handleInputChange("gender_distribution_female", e.target.value)}
                    placeholder="e.g., 55"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Assets pledged as security must be worth at least 1.5x the loan amount requested.
                </p>
              </div>
            </div>

            {formData.collateral_items.map((item, index) => (
              <Card key={index} className="bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Collateral Item {index + 1}</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Asset Type *</Label>
                    <Input
                      value={item.asset_type}
                      onChange={(e) => handleCollateralChange(index, "asset_type", e.target.value)}
                      placeholder="e.g., Vehicle, Property, Equipment"
                    />
                  </div>
                  <div>
                    <Label>Registered Owner</Label>
                    <Input
                      value={item.registered_owner}
                      onChange={(e) => handleCollateralChange(index, "registered_owner", e.target.value)}
                      placeholder="Owner name"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => handleCollateralChange(index, "description", e.target.value)}
                      placeholder="Description including serial numbers, registration numbers"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Market Value (USD) *</Label>
                    <Input
                      type="number"
                      value={item.market_value}
                      onChange={(e) => handleCollateralChange(index, "market_value", e.target.value)}
                      placeholder="Current market value"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button type="button" variant="outline" onClick={addCollateralItem}>
              + Add Another Collateral Item
            </Button>
          </div>
        );

      case 7:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Please upload the required supporting documents. Accepted formats: PDF, Word, Images (max 10MB each).
            </p>
            
            <div className="grid grid-cols-1 gap-3">
              {DOCUMENT_CATEGORIES.map((category) => (
                <div 
                  key={category.id}
                  className={`border rounded-lg p-4 ${
                    documents[category.id] ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className={`h-5 w-5 ${documents[category.id] ? 'text-emerald-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="font-medium text-sm">
                          {category.name}
                          {category.required && <span className="text-red-500 ml-1">*</span>}
                        </p>
                        {documents[category.id] && (
                          <p className="text-xs text-emerald-600">
                            {documents[category.id].name} ({formatFileSize(documents[category.id].size)})
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {documents[category.id] ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeDocument(category.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileUpload(category.id, e.target.files?.[0])}
                          disabled={uploadingDoc === category.id}
                        />
                        <Button variant="outline" size="sm" className="pointer-events-none">
                          {uploadingDoc === category.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload
                            </>
                          )}
                        </Button>
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">
                Documents Uploaded: {Object.keys(documents).length} / {DOCUMENT_CATEGORIES.length}
              </p>
            </div>
          </div>
        );

      case 8:
        // Review Step
        const getTenureDisplay = () => {
          if (formData.tenure_months === "custom") return `${formData.custom_tenure} months (custom)`;
          return `${formData.tenure_months} months`;
        };
        
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-blue-800 font-medium">Review Your Application</p>
                  <p className="text-blue-700 text-sm mt-1">
                    Please review all information carefully before proceeding to submit.
                  </p>
                </div>
              </div>
            </div>

            {/* Institution Information Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Institution Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Institution Name:</span>
                  <p className="font-medium">{formData.institution_name || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Registration No:</span>
                  <p className="font-medium">{formData.company_reg_number || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-500">License Number:</span>
                  <p className="font-medium">{formData.license_number || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-500">License Type:</span>
                  <p className="font-medium">{formData.license_type || "N/A"}</p>
                </div>
              </CardContent>
            </Card>

            {/* Funding Details Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Funding Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Amount Requested:</span>
                  <p className="font-medium text-green-700">${parseFloat(formData.amount_required || 0).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Loan Tenure:</span>
                  <p className="font-medium">{getTenureDisplay()}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Purpose:</span>
                  <p className="font-medium">{formData.purpose_of_loan?.substring(0, 100) || "N/A"}...</p>
                </div>
              </CardContent>
            </Card>

            {/* Financial Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Financial Position
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Total Assets:</span>
                  <p className="font-medium">${parseFloat(formData.total_assets || 0).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Total Liabilities:</span>
                  <p className="font-medium">${parseFloat(formData.total_liabilities || 0).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Capital:</span>
                  <p className="font-medium">${parseFloat(formData.capital || 0).toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>

            {/* Documents Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Documents Uploaded
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  <span className="font-medium text-green-700">{Object.keys(documents).length}</span> document(s) uploaded
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Declaration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-700">
                <p>
                  I hereby confirm that all information provided in this application is true and correct 
                  to the best of my knowledge. I understand that any false statement may result in 
                  immediate denial of this loan request.
                </p>
                <p>
                  I authorize Tzur Finance (Pvt) Ltd to obtain any relevant information required for 
                  loan processing, including inquiries from banks, financial bureaus, suppliers, and 
                  other creditors. I release these institutions from liability under any applicable law.
                </p>
              </CardContent>
            </Card>

            <div>
              <Label>Non-Refundable Application Fee (USD)</Label>
              <Input
                type="number"
                value={formData.application_fee_amount}
                onChange={(e) => handleInputChange("application_fee_amount", e.target.value)}
                placeholder="Enter fee amount paid"
                className="mt-2"
              />
            </div>

            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  checked={formData.consent_identity_verification}
                  onCheckedChange={(checked) => handleInputChange("consent_identity_verification", checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  I consent to Tzur Finance (Pvt) Ltd verifying the identity of our institution and directors through 
                  relevant government databases and third-party verification services. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  checked={formData.consent_credit_bureau}
                  onCheckedChange={(checked) => handleInputChange("consent_credit_bureau", checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  I consent to Tzur Finance (Pvt) Ltd conducting credit checks through registered credit bureaus 
                  and accessing our institution's credit history for the purpose of assessing this loan application. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  checked={formData.declaration_agreed}
                  onCheckedChange={(checked) => handleInputChange("declaration_agreed", checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  I have read, understood, and agree to the above declarations and authorize Tzur Finance 
                  to process this application accordingly. *
                </span>
              </label>
            </div>

            {(!formData.consent_identity_verification || !formData.consent_credit_bureau || !formData.declaration_agreed) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Please provide all required consents to submit your application.
                  </span>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-[#0A2540] mb-2">Application Submitted!</h3>
            <p className="text-gray-600 mb-4">
              Your Microfinance Wholesale Loan application has been received.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Application ID: <span className="font-mono font-semibold">{applicationId}</span>
            </p>
            <p className="text-sm text-gray-600 mb-8">
              Our team will review your application and contact you within 48 hours.
            </p>
            <Link to="/dashboard">
              <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        );
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <CardTitle>Login Required</CardTitle>
            <CardDescription>Please sign in to apply for a Microfinance Wholesale Loan</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Link to="/login">
              <Button className="w-full">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="w-full">Create Account</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg px-2 py-1 shadow-sm border border-gray-100">
                <img src={LOGO_URL} alt="Tzur Finances" className="h-8 object-contain" />
              </div>
            </Link>
            <Link to="/wholesale-lending">
              <Button variant="ghost" className="text-gray-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Wholesale Lending
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      {step <= totalSteps && (
        <div className="bg-white border-b">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Step {step} of {totalSteps}</span>
              <span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#0A2540] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {step <= totalSteps && (
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#0A2540]/10 mb-4">
              <StepIcon className="h-7 w-7 text-[#0A2540]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0A2540]">{getStepTitle()}</h2>
            <p className="text-gray-500 mt-1">Microfinance Wholesale Loan Application</p>
          </div>
        )}

        <Card className="shadow-sm">
          <CardContent className="p-6">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        {step <= totalSteps && (
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            {step < totalSteps ? (
              <Button onClick={handleNext} className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={loading || !formData.declaration_agreed || !formData.consent_identity_verification || !formData.consent_credit_bureau}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Submit Application
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default WholesaleApplication;
